export type ModerationItem = {
  id: number;
  session_id: string;
  user_message: string;
  bot_answer: string;
  status: string;
  created_at: string;
  reviewed_at?: string | null;
  user_message_id?: number | null;
  bot_message_id?: number | null;
};

export type ExcludedThread = {
  thread_root_id: number;
  excluded_at: string;
  reason?: string | null;
};

async function adminFetch(path: string, init?: RequestInit) {
  const res = await fetch(path, {
    ...init,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers || {}),
    },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `HTTP ${res.status}`);
  }
  if (res.status === 204) return null;
  return res.json();
}

export function fetchModeration(status = "pending") {
  const q = new URLSearchParams({ status, limit: "100" });
  return adminFetch(`/api/admin/moderation?${q}`) as Promise<{ items: ModerationItem[] }>;
}

export function approveModeration(id: number) {
  return adminFetch(`/api/admin/moderation/${id}/approve`, { method: "POST" });
}

export function rejectModeration(id: number) {
  return adminFetch(`/api/admin/moderation/${id}/reject`, { method: "POST" });
}

export function fetchExcludedThreads() {
  return adminFetch("/api/admin/threads/excluded") as Promise<ExcludedThread[]>;
}

export function excludeThread(threadRootId: number, reason = "") {
  return adminFetch("/api/admin/threads/exclude", {
    method: "POST",
    body: JSON.stringify({ thread_root_id: threadRootId, reason }),
  });
}

export function restoreThread(threadRootId: number) {
  return adminFetch(`/api/admin/threads/${threadRootId}`, { method: "DELETE" });
}

export type PlanningMessage = {
  id: string;
  date: string;
  author: string;
  text: string;
  author_kind?: "user" | "bot" | "service";
  source?: string;
  reply_to?: string;
  enqueued_at: string;
  source_date?: string | null;
};

export function fetchPlanningMessages(limit = 500) {
  return adminFetch(
    `/api/admin/planning/messages?limit=${limit}`
  ) as Promise<{ messages: PlanningMessage[]; count: number }>;
}

export function hideFeedMessage(messageId: string) {
  return adminFetch(`/api/admin/messages/${encodeURIComponent(messageId)}`, {
    method: "DELETE",
  }) as Promise<{ message_id: number; hidden_at: string }>;
}

export type PublishQueueStatus = {
  feed_published_count: number;
  feed_queued_count: number;
  is_night_now: boolean;
  night_hours: string;
  queue_interval_minutes: string;
  last_month_days: number;
  next_publish_at?: string | null;
  last_publish_at?: string | null;
  minutes_until_next?: number | null;
  next_queued_message_id?: number | null;
  next_queued_message_date?: string | null;
};

export type ImportJsonResult = {
  source: string;
  chat_title?: string;
  chat_id?: number | null;
  domain_label?: string;
  domain_keywords?: string;
  feed_upserted: number;
  feed_published_immediate?: number;
  feed_queued?: number;
  queue_last_month?: boolean;
  hide_chat_source?: boolean;
  feed_published_count?: number;
  feed_queued_count?: number;
  publish_queue?: PublishQueueStatus | null;
  rag_chunks_added: number;
  rag_chunks_skipped: number;
  rag_chunks_path: string;
  etl: Record<string, unknown> | null;
};

export function fetchPublishQueueStatus() {
  return adminFetch(
    "/api/admin/publish-queue/status"
  ) as Promise<PublishQueueStatus>;
}

export type RagChunksStats = {
  rag_chunks_path: string;
  file_exists: boolean;
  file_bytes: number;
  total: number;
  telegram: number;
  articles: number;
  other: number;
};

export function fetchRagChunksStats() {
  return adminFetch("/api/admin/rag-chunks/stats") as Promise<RagChunksStats>;
}

export function publishQueueNow() {
  return adminFetch("/api/admin/publish-queue/publish-now", {
    method: "POST",
  }) as Promise<{
    status: string;
    published_message_id?: number;
    published_message_date?: string;
    remaining_queued?: number;
    already_in_feed?: boolean;
    queue?: PublishQueueStatus;
  }>;
}

export function publishAllQueued() {
  return adminFetch("/api/admin/publish-queue/publish-all", {
    method: "POST",
  }) as Promise<{
    status: string;
    published_count?: number;
    published_message_ids?: number[];
    remaining_queued?: number;
    queue?: PublishQueueStatus;
  }>;
}

export type AdminArticle = {
  slug: string;
  url: string;
  title?: string | null;
  social_enabled: boolean;
  views_count: number;
  likes_count: number;
  comments_approved: number;
  comments_pending: number;
  updated_at: string;
  rag_indexed?: boolean;
  rag_chunks_added?: number;
  rag_chunks_removed?: number;
  rag_warning?: string | null;
};

export type AdminArticleComment = {
  id: number;
  article_slug: string;
  kind: string;
  status: string;
  author_display: string;
  question?: string | null;
  answer: string;
  created_at: string;
  reviewed_at?: string | null;
};

export function fetchAdminArticles() {
  return adminFetch("/api/admin/articles") as Promise<{ articles: AdminArticle[] }>;
}

export function fetchArticleComments(opts: {
  status?: string;
  slug?: string;
  limit?: number;
}) {
  const q = new URLSearchParams();
  if (opts.status != null && opts.status !== "") q.set("status", opts.status);
  if (opts.slug) q.set("slug", opts.slug);
  if (opts.limit) q.set("limit", String(opts.limit));
  return adminFetch(`/api/admin/articles/comments?${q}`) as Promise<{
    comments: AdminArticleComment[];
  }>;
}

export function approveArticleComment(id: number) {
  return adminFetch(`/api/admin/articles/comments/${id}/approve`, { method: "POST" });
}

export function rejectArticleComment(id: number) {
  return adminFetch(`/api/admin/articles/comments/${id}/reject`, { method: "POST" });
}

export function deleteArticleComment(id: number) {
  return adminFetch(`/api/admin/articles/comments/${id}`, { method: "DELETE" });
}

export function patchArticleComment(
  id: number,
  body: {
    question?: string | null;
    answer?: string;
    author_display?: string;
  }
) {
  return adminFetch(`/api/admin/articles/comments/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  }) as Promise<AdminArticleComment>;
}

export function upsertAdminArticle(body: {
  slug: string;
  url?: string;
  title?: string;
  social_enabled?: boolean;
  views_count?: number;
  likes_count?: number;
  markdown?: string;
  fetch_from_url?: boolean;
}) {
  return adminFetch("/api/admin/articles", {
    method: "POST",
    body: JSON.stringify(body),
  }) as Promise<AdminArticle>;
}

export type GenerateArticleCommentsResult = {
  slug: string;
  article_title?: string | null;
  requested: number;
  generated: number;
  comments: Array<{
    id: number;
    question?: string | null;
    answer: string;
    author_display: string;
    source_thread_id?: number | null;
    chunk_id?: string | null;
  }>;
  errors: string[];
  search_terms?: string[];
};

export function generateArticleComments(slug: string, count: number) {
  return adminFetch("/api/admin/articles/generate-comments", {
    method: "POST",
    body: JSON.stringify({ slug, count }),
  }) as Promise<GenerateArticleCommentsResult>;
}

export function patchAdminArticle(
  slug: string,
  body: {
    url?: string;
    title?: string;
    social_enabled?: boolean;
    views_count?: number;
    likes_count?: number;
  }
) {
  const q = new URLSearchParams({ slug });
  return adminFetch(`/api/admin/articles?${q}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  }) as Promise<AdminArticle>;
}

export function importJsonPayload(
  jsonText: string,
  queueLastMonth = false,
  hideChatSource = false
) {
  const q = queueLastMonth ? "?queue_last_month=true" : "";
  let body = jsonText;
  try {
    const parsed = JSON.parse(jsonText.trim());
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      parsed.queue_last_month = queueLastMonth;
      parsed.hide_chat_source = hideChatSource;
      body = JSON.stringify(parsed);
    }
  } catch {
    /* отправим как есть */
  }
  return adminFetch(`/api/admin/import/json${q}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
  }) as Promise<ImportJsonResult>;
}
